<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useGlobalSearchStore } from 'src/stores';
import { useApi } from 'src/stores/useApis';
import { getCurrentInstance } from 'vue';
import type { SearchType } from 'src/types';
import { onBeforeUnmount } from 'vue';

const globalSearchStore = useGlobalSearchStore();
const { searchText, currentSearchTab } = storeToRefs(globalSearchStore);
const apiStore = useApi();

const inputRef = ref<any>(null);
const focusInput = () => {
    if (inputRef.value?.focusInput) {
        inputRef.value.focusInput();
    }
};
defineExpose({
    focus: focusInput,
    blur: () => {
        inputRef.value?.blur();
    }
});
const getRootInstance = (): HTMLElement => {
    const instance = getCurrentInstance();
    const res: HTMLElement = instance?.appContext.config.globalProperties?.rootElement as unknown as HTMLElement;
    return res;
};
const rootEle = ref<HTMLElement | undefined>();
const handleEscKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
        if (searchText.value.length > 0) {
            searchText.value = '';
            focusInput();
        } else closePopup();
    } else if (e.key === 'Tab') {
        e.preventDefault();
        e.stopPropagation();
        const tablist: SearchType[] = ['recent_accessed', 'people', 'item', 'entity'];
        const index = tablist.indexOf(currentSearchTab.value);
        if (e.shiftKey) {
            const prevIndex = (index - 1 + tablist.length) % tablist.length;
            currentSearchTab.value = tablist[prevIndex] as SearchType;
            focusInput();
        } else {
            const nextIndex = (index + 1) % tablist.length;
            currentSearchTab.value = tablist[nextIndex] as SearchType;
            focusInput();
        }
    }
};

onMounted(() => {
    window.addEventListener('keydown', handleEscKey);
    nextTick(() => focusInput());
    rootEle.value = getRootInstance();
});

onBeforeUnmount(() => {
    window.removeEventListener('keydown', handleEscKey);
});
const closePopup = async () => {
    // @ts-ignore
    window?.appManager?.hide();
    await apiStore.cancelAllRequests();
    globalSearchStore.resetSearch();
    globalSearchStore.resetFilter();
    currentSearchTab.value = 'recent_accessed';
};
const searchPlaceHolder = computed(() => {
    const searchPlace: Record<SearchType, string> = {
        recent_accessed: 'Search...',
        people: 'Search by display name, email...',
        item: 'Search by name, code...',
        entity: 'Search by name, code...'
    };
    return searchPlace[currentSearchTab.value as SearchType];
});
watch(currentSearchTab, () => {
    nextTick(() => {
        focusInput();
    });
});
</script>

<template>
    <div class="global-search-header-container">
        <ArboInput ref="inputRef" v-model="searchText" class="arbo-ui-styles" :placeholder="searchPlaceHolder">
            <template #append>
                <ArboIcon
                    v-if="searchText.length == 0"
                    class="search-icon arbo-ui-styles"
                    v-bind="{
                        name: 'MagnifyingGlass'
                    }" />
                <ArboButton
                    v-else
                    class="clear-search"
                    :prefix-icon="'XMarkSolid'"
                    :prefix-tooltip="'Clear search'"
                    size="small"
                    :label="''"
                    @click="searchText = ''">
                </ArboButton>
            </template>
        </ArboInput>

        <!-- <ArboButton
            class="arbo-ui-styles"
            v-bind="{
                prefixIcon: 'XMarkSolid',
                label: '',
                type: 'tertiary',
                prefixTooltip: 'Close'
            }"
            @click="closePopup" /> -->
    </div>
</template>
<style scoped lang="scss">
.global-search-main .arbo-btn.clear-search {
    height: 18px !important;
    width: 18px;
    background-color: var(--primary-color);
    border-radius: 50%;
    overflow: hidden;
    color: white;
    background-color: var(--primary-color);

    :deep(.arbo__icon-btn.small) {
        height: 12px !important;
        width: 12px;
        padding: 2px;
    }
}
</style>
